// 📊 처리 이력 탭 - 상태 변경 로그 및 알림톡 내역
import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Avatar,
  Divider,
  Grid,
  Card,
  CardContent,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  LocalShipping as ShippingIcon,
  Assessment as AssessmentIcon,
  MonetizationOn as MoneyIcon,
  Send as SendIcon,
  Person as PersonIcon,
  Inventory as InventoryIcon,
  Cancel as CancelIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';

import type { Application } from '../../types/application';
import { formatDate } from '../orderDetail/utils';

interface ProcessHistoryTabProps {
  application: Application;
}

// 🎯 상태별 아이콘 및 색상 매핑
const getStatusConfig = (status: string) => {
  const configs: Record<string, { icon: React.ReactElement; color: string; bgcolor: string }> = {
    '접수완료': {
      icon: <CheckIcon />,
      color: '#1976d2',
      bgcolor: '#e3f2fd',
    },
    '택배접수': {
      icon: <ShippingIcon />,
      color: '#29b6f6',
      bgcolor: '#e1f5fe',
    },
    '입고완료': {
      icon: <InventoryIcon />,
      color: '#00bcd4',
      bgcolor: '#e0f2f1',
    },
    '검수중': {
      icon: <AssessmentIcon />,
      color: '#7e57c2',
      bgcolor: '#f3e5f5',
    },
    '가격안내': {
      icon: <MoneyIcon />,
      color: '#ffb300',
      bgcolor: '#fff8e1',
    },
    '입금대기': {
      icon: <ScheduleIcon />,
      color: '#26a69a',
      bgcolor: '#e0f2f1',
    },
    '부분완료': {
      icon: <TrendingUpIcon />,
      color: '#66bb6a',
      bgcolor: '#e8f5e8',
    },
    '반송': {
      icon: <CancelIcon />,
      color: '#ef5350',
      bgcolor: '#ffebee',
    },
    '완료': {
      icon: <CheckIcon />,
      color: '#43a047',
      bgcolor: '#e8f5e8',
    },
  };

  return configs[status] || {
    icon: <ScheduleIcon />,
    color: '#757575',
    bgcolor: '#f5f5f5',
  };
};

// 📈 진행 단계 컴포넌트
const ProgressSteps: React.FC<{ application: Application }> = ({ application }) => {
  const allSteps = [
    '접수완료',
    '택배접수', 
    '입고완료',
    '검수중',
    '가격안내',
    '입금대기',
    '완료'
  ];

  const currentStepIndex = allSteps.indexOf(application.status);
  
  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid #e1e8ed',
        borderRadius: 3,
        mb: 4,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          p: 2.5,
          bgcolor: 'primary.main',
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <AssessmentIcon />
          <Typography variant="h6" fontWeight={700}>
            처리 진행 단계
          </Typography>
        </Stack>
      </Box>

      <CardContent sx={{ p: 3 }}>
        <Stepper 
          activeStep={currentStepIndex} 
          orientation="horizontal"
          sx={{
            '& .MuiStepConnector-line': {
              borderColor: '#e0e0e0',
              borderTopWidth: 3,
            },
            '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
              borderColor: 'primary.main',
            },
            '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
              borderColor: 'primary.main',
            },
            '& .MuiStep-root': {
              px: 1,
            },
            '& .MuiStepLabel-root': {
              flexDirection: 'column',
            },
            '& .MuiStepLabel-labelContainer': {
              mt: 1,
            },
          }}
        >
          {allSteps.map((step, index) => {
            const config = getStatusConfig(step);
            const isCompleted = index < currentStepIndex;
            const isCurrent = index === currentStepIndex;
            
            return (
              <Step key={step} completed={isCompleted}>
                <StepLabel
                  StepIconComponent={() => (
                    <Avatar
                      sx={{
                        bgcolor: isCompleted || isCurrent ? config.color : '#e0e0e0',
                        width: isCurrent ? 40 : 32,
                        height: isCurrent ? 40 : 32,
                        border: isCurrent ? `3px solid ${config.color}` : 'none',
                        boxShadow: isCurrent ? `0 0 12px ${config.color}40` : 'none',
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {React.cloneElement(config.icon as React.ReactElement, {
                        sx: { 
                          fontSize: isCurrent ? 20 : 16, 
                          color: isCompleted || isCurrent ? 'white' : '#757575' 
                        }
                      })}
                    </Avatar>
                  )}
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontWeight: isCurrent ? 700 : isCompleted ? 600 : 400,
                      color: isCurrent ? 'primary.main' : isCompleted ? 'text.primary' : 'text.secondary',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }, // 가로 레이아웃에 맞게 크기 조정
                      textAlign: 'center',
                      lineHeight: 1.2,
                    },
                  }}
                >
                  <Box sx={{ textAlign: 'center', maxWidth: { xs: 60, sm: 80 } }}>
                    <Typography
                      variant="body2"
                      fontWeight={isCurrent ? 700 : isCompleted ? 600 : 400}
                      color={isCurrent ? 'primary.main' : isCompleted ? 'text.primary' : 'text.secondary'}
                      sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        lineHeight: 1.2,
                        wordBreak: 'keep-all', // 한글 단어 단위로 줄바꿈
                      }}
                    >
                      {step}
                    </Typography>
                    {isCurrent && (
                      <Typography 
                        variant="caption" 
                        color="primary.main" 
                        fontWeight={600}
                        sx={{ 
                          display: 'block', 
                          mt: 0.5,
                          fontSize: { xs: '0.6rem', sm: '0.7rem' },
                        }}
                      >
                        현재
                      </Typography>
                    )}
                  </Box>
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </CardContent>
    </Card>
  );
};

// 📝 기기별 이력 컴포넌트
const DeviceHistory: React.FC<{ application: Application }> = ({ application }) => {
  const devices = application.devices || [];

  if (devices.length === 0) {
    return null;
  }

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid #e1e8ed',
        borderRadius: 3,
        mb: 4,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          p: 2.5,
          bgcolor: '#4caf50',
          background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
          color: 'white',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <InventoryIcon />
          <Typography variant="h6" fontWeight={700}>
            기기별 처리 현황
          </Typography>
        </Stack>
      </Box>

      <CardContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {devices.map((device, index) => {
            const config = getStatusConfig(device.status);
            
            return (
              <Grid item xs={12} md={6} key={device.id}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    border: '1px solid #e1e8ed',
                    borderRadius: 2,
                    bgcolor: config.bgcolor,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <Avatar
                      sx={{
                        bgcolor: config.color,
                        width: 32,
                        height: 32,
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      {index + 1}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {device.deviceModel}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {device.deviceType}
                      </Typography>
                    </Box>
                    <Chip
                      icon={typeof config.icon === 'string' ? undefined : config.icon}
                      label={device.status}
                      size="small"
                      sx={{
                        bgcolor: config.color,
                        color: 'white',
                        fontWeight: 600,
                        '& .MuiChip-icon': { color: 'white' },
                      }}
                    />
                  </Stack>

                  <Stack spacing={1.5}>
                    {device.price_offer && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">
                          검수 가격
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ color: '#ff9800' }}>
                          ₩{device.price_offer.toLocaleString()}
                        </Typography>
                      </Stack>
                    )}
                    
                    {device.final_price && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">
                          최종 가격
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ color: '#4caf50' }}>
                          ₩{device.final_price.toLocaleString()}
                        </Typography>
                      </Stack>
                    )}

                    {device.grade && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">
                          등급
                        </Typography>
                        <Chip
                          label={device.grade}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 500, fontSize: '0.75rem' }}
                        />
                      </Stack>
                    )}

                    {device.updated_at && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">
                          최종 수정
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(device.updated_at)}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
    </Card>
  );
};

// 🎯 메인 컴포넌트
const ProcessHistoryTab: React.FC<ProcessHistoryTabProps> = ({ application }) => {
  return (
    <Box>
      {/* 📈 진행 단계 표시 */}
      <ProgressSteps application={application} />

      {/* 📱 기기별 이력 */}
      <DeviceHistory application={application} />

      {/* 📊 요약 정보 */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              border: '1px solid #e1e8ed',
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                p: 2.5,
                bgcolor: '#ff9800',
                background: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
                color: 'white',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <MoneyIcon />
                <Typography variant="h6" fontWeight={700}>
                  금액 정보
                </Typography>
              </Stack>
            </Box>

            <CardContent sx={{ p: 3 }}>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    총 검수금액
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#ff9800' }}>
                    ₩{(application.total_price_offer || 0).toLocaleString()}
                  </Typography>
                </Stack>
                
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    총 최종금액
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#4caf50' }}>
                    ₩{(application.total_final_price || 0).toLocaleString()}
                  </Typography>
                </Stack>

                <Divider sx={{ my: 1 }} />

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    차액
                  </Typography>
                  <Typography 
                    variant="body1" 
                    fontWeight={600}
                    sx={{ 
                      color: (application.total_final_price || 0) >= (application.total_price_offer || 0) 
                        ? '#4caf50' 
                        : '#f44336' 
                    }}
                  >
                    ₩{((application.total_final_price || 0) - (application.total_price_offer || 0)).toLocaleString()}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              border: '1px solid #e1e8ed',
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                p: 2.5,
                bgcolor: '#9c27b0',
                background: 'linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)',
                color: 'white',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <PersonIcon />
                <Typography variant="h6" fontWeight={700}>
                  처리 정보
                </Typography>
              </Stack>
            </Box>

            <CardContent sx={{ p: 3 }}>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    신청일시
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatDate(application.created_at)}
                  </Typography>
                </Stack>

                {application.updated_at && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      최종 수정
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatDate(application.updated_at)}
                    </Typography>
                  </Stack>
                )}

                {application.updated_at && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      담당자
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      시스템
                    </Typography>
                  </Stack>
                )}

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    처리 기기 수
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {application.devices?.length || 0}개
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 🔔 알림톡 이력은 별도 구현 필요 */}
      <Box sx={{ mt: 4 }}>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          <Typography variant="body2" fontWeight={600}>
            📨 알림톡 전송 이력
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            알림톡 전송 내역은 "알림톡" 탭에서 확인하실 수 있습니다.
          </Typography>
        </Alert>
      </Box>
    </Box>
  );
};

export default ProcessHistoryTab;